import React, { createContext, useContext, useMemo, useState } from "react";
import type { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import {
  addPaymentMethod,
  deletePaymentMethod,
  updateMyEmail,
  updateSelectedPaymentMethod,
} from "@/api/users";
import { createSubscriptionPayment } from "@/api/subscriptions";
import { useAuth } from "@/store/authStore";
import { isTelegramEnv } from "@/utils/helpers";

import { TARIFF_ID, TG_SUPPORT_LINK } from "../constants";
import { requireContext, WithChildren } from "./shared";

type MainActionsContextValue = {
  isPayLoading: boolean;
  isCardLoading: boolean;
  selectingPaymentMethodId: string | null;
  deletingPaymentMethodId: string | null;
  paymentMethodsError: string;
  emailModalOpen: boolean;
  closeEmailModal: () => void;
  onBuyClick: () => Promise<void>;
  onEmailSubmit: (email: string) => Promise<void>;
  onAddCard: () => Promise<void>;
  onSelectCard: (paymentMethodId: string) => Promise<void>;
  onDeleteCard: (paymentMethodId: string) => Promise<void>;
  onSupport: () => void;
};

const MainActionsContext = createContext<MainActionsContextValue | null>(null);

export function useMainActionsContext() {
  return requireContext(
    useContext(MainActionsContext),
    "useMainActionsContext"
  );
}

export function MainActionsProvider({ children }: WithChildren) {
  const navigate = useNavigate();
  const me = useAuth((s) => s.me);
  const setMe = useAuth((s) => s.setMe);
  const accessToken = useAuth((s) => s.accessToken);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [isPayLoading, setIsPayLoading] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [selectingPaymentMethodId, setSelectingPaymentMethodId] = useState<
    string | null
  >(null);
  const [deletingPaymentMethodId, setDeletingPaymentMethodId] = useState<
    string | null
  >(null);
  const [paymentMethodsError, setPaymentMethodsError] = useState("");
  const [pendingEmailAction, setPendingEmailAction] = useState<
    "buy" | "card" | null
  >(null);

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<{ error?: string }>;
    return axiosError.response?.data?.error || fallback;
  };

  const openPaymentLink = (url: string) => {
    window.location.href = url;
  };

  const runCreatePayment = async () => {
    const data = await createSubscriptionPayment(TARIFF_ID);
    openPaymentLink(data.confirmation_url);
  };

  const runAddPaymentMethod = async () => {
    const data = await addPaymentMethod();
    openPaymentLink(data.confirmation_url);
  };

  const onBuyClick = async () => {
    setPaymentMethodsError("");

    const inTelegram = isTelegramEnv();
    const isTelegramUser = me?.auth_provider === "telegram";

    if (!inTelegram && !accessToken) {
      navigate("/auth");
      return;
    }

    if (isTelegramUser && me && !me.receipt_email) {
      setPendingEmailAction("buy");
      setEmailModalOpen(true);
      return;
    }

    if (isPayLoading) return;

    setIsPayLoading(true);

    try {
      await runCreatePayment();
    } catch (error) {
      setIsPayLoading(false);
      throw error;
    }
  };

  const onEmailSubmit = async (email: string) => {
    if (isPayLoading || isCardLoading) return;
    setPaymentMethodsError("");

    const isCardAction = pendingEmailAction === "card";

    if (isCardAction) {
      setIsCardLoading(true);
    } else {
      setIsPayLoading(true);
    }

    try {
      const updated = await updateMyEmail(email);
      setMe(updated);

      if (isCardAction) {
        await runAddPaymentMethod();
      } else {
        await runCreatePayment();
      }

      setEmailModalOpen(false);
      setPendingEmailAction(null);
    } catch (error) {
      setIsPayLoading(false);
      setIsCardLoading(false);
      setPendingEmailAction(null);
      throw error;
    }
  };

  const onAddCard = async () => {
    if (!me || isCardLoading) return;
    setPaymentMethodsError("");

    if (!me.receipt_email && !me.email) {
      setPendingEmailAction("card");
      setEmailModalOpen(true);
      return;
    }

    setIsCardLoading(true);

    try {
      await runAddPaymentMethod();
    } catch (error) {
      setIsCardLoading(false);
      throw error;
    }
  };

  const onSelectCard = async (paymentMethodId: string) => {
    if (
      !me ||
      isCardLoading ||
      deletingPaymentMethodId ||
      selectingPaymentMethodId ||
      me.selected_payment_method_id === paymentMethodId
    ) {
      return;
    }

    try {
      setPaymentMethodsError("");
      setSelectingPaymentMethodId(paymentMethodId);

      const updated = await updateSelectedPaymentMethod(paymentMethodId);
      setMe({
        ...updated,
        selected_payment_method_id: paymentMethodId,
        payment_methods: (updated.payment_methods?.length
          ? updated.payment_methods
          : me.payment_methods
        ).map((method) => ({
          ...method,
          is_selected: method.payment_method_id === paymentMethodId,
        })),
      });
    } finally {
      setSelectingPaymentMethodId(null);
    }
  };

  const onDeleteCard = async (paymentMethodId: string) => {
    if (
      !me ||
      isCardLoading ||
      selectingPaymentMethodId ||
      deletingPaymentMethodId
    ) {
      return;
    }

    setPaymentMethodsError("");

    try {
      setDeletingPaymentMethodId(paymentMethodId);
      const updated = await deletePaymentMethod(paymentMethodId);
      setMe(updated);
    } catch (error) {
      setPaymentMethodsError(
        getApiErrorMessage(error, "Не удалось удалить карту")
      );
    } finally {
      setDeletingPaymentMethodId(null);
    }
  };

  const value = useMemo<MainActionsContextValue>(
    () => ({
      isPayLoading,
      isCardLoading,
      selectingPaymentMethodId,
      deletingPaymentMethodId,
      paymentMethodsError,
      emailModalOpen,
      closeEmailModal: () => setEmailModalOpen(false),
      onBuyClick,
      onEmailSubmit,
      onAddCard,
      onSelectCard,
      onDeleteCard,
      onSupport: () => {
        window.open(TG_SUPPORT_LINK, "_blank", "noopener,noreferrer");
      },
    }),
    [
      deletingPaymentMethodId,
      emailModalOpen,
      isCardLoading,
      isPayLoading,
      paymentMethodsError,
      selectingPaymentMethodId,
    ]
  );

  return (
    <MainActionsContext.Provider value={value}>
      {children}
    </MainActionsContext.Provider>
  );
}
